<template>
  <div class="theme-switcher">
    <label class="theme-label" :for="selectId">Theme</label>
    <select
      :id="selectId"
      class="theme-select"
      :value="theme"
      :disabled="saving"
      @change="onChange"
    >
      <option v-for="opt in BUI_THEME_OPTIONS" :key="opt.id" :value="opt.id">
        {{ opt.label }}
      </option>
    </select>
  </div>
</template>

<script lang="ts" setup>
import { ref, getCurrentInstance } from 'vue';
import useTheme from './theme';
import useNotifications from '@/components/Notifications/composables/notifications';
import { isAllowedUiTheme } from './themeConstants';
import { BUI_THEME_OPTIONS } from './themeRegistry';

const selectId = `ui-theme-select-${getCurrentInstance()?.uid ?? 0}`;

const { theme, setTheme } = useTheme();
const notification = useNotifications();
const saving = ref(false);

async function onChange(event: Event) {
  const select = event.target as HTMLSelectElement;
  const next = select.value;
  if (!isAllowedUiTheme(next)) {
    return;
  }
  if (next === theme.value) {
    return;
  }
  saving.value = true;
  try {
    await setTheme(next);
  } catch {
    select.value = theme.value;
    notification.addNotification('Could not save theme preference.', { type: 'failure' });
  } finally {
    saving.value = false;
  }
}
</script>

<style lang="scss" scoped>
/* Styled for BUI card surfaces (lives in the Account Editor's Appearance
   card); uses the same tokens as InfoCard so every theme works. */
.theme-switcher {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  max-width: 320px;
  box-sizing: border-box;
}

.theme-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: color-mix(in srgb, var(--bui-tbl-row-text, #2a2969) 70%, transparent);
}

.theme-select {
  width: 100%;
  max-width: 100%;
  font-size: 13px;
  font-family: inherit;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid color-mix(in srgb, var(--bui-tbl-row-text, #2a2969) 25%, transparent);
  background: var(--bui-tbl-row-fill, #fffcfd);
  color: var(--bui-tbl-row-text, #2a2969);
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}
</style>
